import React from 'react';
import omit from 'lodash/omit';
import { Switch } from '@mantine/core';
import { puckSectionField } from './puckSectionField';
import { puckVisibilityField } from './puckVisibilityField';
import { useSelectedItemProps } from './useSelectedItemProps';
import { isWebTemplateCatalog } from '../../utils/variableCatalogRegistry';
import { isSiteVariableBox, ownBox, TABLE_FORM_PROP } from '../../utils/siteVariableBoxes';

/**
 * Réglages « Formulaire » d'un bloc Texte, Image ou Tableau dans son panneau : ce que
 * le classique y règle (HideInFormSwitcher, tuile Masquer). Le nom, la
 * remarque et les images proposées se règlent dans l'onglet Formulaire du
 * modèle, comme dans son créateur de formulaire. Ils ne se posent que dans un
 * template : un site hérite du choix de son modèle.
 *
 * Une image règle son switch dans son propre champ (`MediaUploadField`), où
 * qu'elle soit : seuls le texte et le tableau, qui n'ont pas de champ unique,
 * ont leur section.
 */

// Tant que l'admin n'a pas tranché, le switch montre le défaut (lié au CRM =
// dans le formulaire) plutôt qu'un « non » que le formulaire démentirait.
function ShowInFormSwitch({ value, onChange }) {
  const { selectedItem } = useSelectedItemProps();
  return (
    <Switch
      size="sm"
      label="Afficher dans le formulaire"
      checked={isSiteVariableBox(ownBox(selectedItem))}
      onChange={(e) => onChange({ ...value, showInForm: e.currentTarget.checked })}
    />
  );
}

const TABLE_FORM_FIELDS = {
  _sectionForm: puckSectionField('Formulaire'),
  [TABLE_FORM_PROP]: {
    type: 'custom',
    label: 'Afficher dans le formulaire',
    render: ({ value, onChange }) => <ShowInFormSwitch value={value} onChange={onChange} />,
  },
};

// « Masquer » du formulaire du classique (hideField) : la tuile de la rangée
// d'actions, que `resolveVariableBoxFields` ne garde que sur une box variable.
export const variableBoxFormFields = ({ withFormSection = false } = {}) => ({
  isVisible: puckVisibilityField(),
  ...(withFormSection ? TABLE_FORM_FIELDS : {}),
});

const FORM_FIELD_KEYS = Object.keys(TABLE_FORM_FIELDS);

/* Puck rappelle `resolveFields` à chaque changement de l'item : un même jeu de
   champs doit rendre le même objet, sinon l'inspecteur se reconstruirait. */
const variantsCache = new WeakMap();

const cachedVariant = (fields, key, build) => {
  if (!variantsCache.has(fields)) variantsCache.set(fields, new Map());
  const variants = variantsCache.get(fields);
  if (!variants.has(key)) variants.set(key, build());
  return variants.get(key);
};

/**
 * Retire la section Formulaire hors template, et la tuile Masquer d'une image
 * ou d'un tableau ordinaire — sauf s'il est déjà masqué, pour qu'on puisse
 * toujours le réafficher. `alwaysHideable` : un bloc qui proposait Masquer
 * avant d'être une box (le Texte) le garde.
 */
export const resolveVariableBoxFields = (block, fields, { alwaysHideable = false } = {}) => {
  const inTemplate = isWebTemplateCatalog();
  const canHide = alwaysHideable || isSiteVariableBox(ownBox(block)) || block.props?.isVisible === false;
  return cachedVariant(fields, `${inTemplate}|${canHide}`, () => omit(fields, [
    ...(inTemplate ? [] : FORM_FIELD_KEYS),
    ...(canHide ? [] : ['isVisible']),
  ]));
};
